import React from 'react';
import { Box, Paper, Skeleton, Typography, alpha } from '@mui/material';
import { BarChart } from '@mui/x-charts';

const PERFORMANCE_ACCENT = '#3388f2';

/**
 * Reusable metric card for dashboards with optional mini trend bars.
 * @param {'default'|'performance'} [variant] — performance matches Performance dashboard chart cards
 */
const DashboardMetricCard = ({
  title,
  value,
  unit,
  data,
  color,
  icon,
  theme = 'light',
  formatValue = (val) => val.toLocaleString(),
  variant = 'default',
  loading = false,
}) => {
  const isDark = theme === 'dark';
  const isPerformance = variant === 'performance';
  const themeColors = {
    text: isDark ? '#e7efff' : '#1e293b',
    textSecondary: isDark ? '#d9e3f0' : '#455468',
    border: isDark ? alpha('#ffffff', 0.08) : alpha(PERFORMANCE_ACCENT, 0.2),
    paper: isDark ? 'rgba(25,27,36,0.96)' : '#ffffff',
  };

  if (isPerformance) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 0,
          overflow: 'hidden',
          position: 'relative',
          height: '100%',
          borderRadius: 3,
          border: `1px solid ${themeColors.border}`,
          background: isDark
            ? 'linear-gradient(160deg, rgba(27,29,39,0.98) 0%, rgba(24,27,37,0.96) 62%, rgba(22,29,43,0.92) 100%)'
            : 'linear-gradient(160deg, #ffffff 0%, #f8fbff 58%, #edf6ff 100%)',
          boxShadow: isDark
            ? `0 14px 34px ${alpha('#000', 0.16)}`
            : `0 18px 42px ${alpha(PERFORMANCE_ACCENT, 0.11)}`,
          transition: 'background-color 0.3s ease, border-color 0.3s ease',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: -58,
            right: -48,
            width: 150,
            height: 150,
            borderRadius: '50%',
            bgcolor: alpha(PERFORMANCE_ACCENT, isDark ? 0.07 : 0.1),
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            left: -54,
            bottom: -70,
            width: 150,
            height: 150,
            borderRadius: '50%',
            bgcolor: alpha('#18c6d8', isDark ? 0.06 : 0.08),
            pointerEvents: 'none',
          }}
        />
        <Box sx={{ p: { xs: 1.75, sm: 2, md: 2.25 }, pb: { xs: 2, sm: 2.5 }, position: 'relative', zIndex: 1 }}>
          <Typography
            variant="overline"
            sx={{
              color: PERFORMANCE_ACCENT,
              fontWeight: 800,
              letterSpacing: '0.12em',
              lineHeight: 1.25,
              display: 'block',
              mb: 1.25,
              fontSize: { xs: '0.62rem', sm: '0.68rem', md: '0.75rem' },
              wordBreak: 'break-word',
            }}
          >
            {title.replace(/\s+/g, ' ').toUpperCase()}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1, sm: 1.5 }, mb: data?.length > 0 ? 1.5 : 0 }}>
            {icon && (
              <Box
                sx={{
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  borderRadius: 2,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(color, isDark ? 0.22 : 0.14),
                  border: `1px solid ${alpha(color, isDark ? 0.34 : 0.2)}`,
                  color,
                  '& .MuiSvgIcon-root': { fontSize: { xs: 20, sm: 22 } },
                }}
              >
                {icon}
              </Box>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                component="div"
                variant="h4"
                sx={{
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color,
                  fontSize: 'clamp(1.05rem, 1.6vw + 0.65rem, 1.75rem)',
                  lineHeight: 1.2,
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {loading ? (
                  <Skeleton
                    variant="text"
                    width="68%"
                    height={38}
                    sx={{ bgcolor: alpha(color, isDark ? 0.18 : 0.12), borderRadius: 1 }}
                  />
                ) : (
                  typeof value === 'number' ? formatValue(value) : value
                )}
              </Typography>
              {unit && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    color: themeColors.textSecondary,
                    fontWeight: 500,
                    textTransform: 'lowercase',
                    letterSpacing: '0.02em',
                  }}
                >
                  {unit}
                </Typography>
              )}
            </Box>
          </Box>

          {(loading || (data && data.length > 0)) && (
            <Box
              sx={{
                width: '100%',
                height: 84,
                mt: 0.5,
                mb: 0.5,
                px: 0.25,
                opacity: isDark ? 0.95 : 1,
                overflow: 'hidden',
              }}
            >
              {loading ? (
                <Skeleton
                  variant="rounded"
                  height={72}
                  sx={{ bgcolor: alpha(color, isDark ? 0.14 : 0.1), borderRadius: 2 }}
                />
              ) : (
                <BarChart
                  dataset={data}
                  height={84}
                  margin={{ top: 8, right: 4, left: 4, bottom: 8 }}
                  xAxis={[{ scaleType: 'band', dataKey: 'name', categoryGapRatio: 0.28 }]}
                  yAxis={[{}]}
                  series={[
                    {
                      dataKey: 'value',
                      color,
                      borderRadius: 6,
                    },
                  ]}
                  grid={{ horizontal: false, vertical: false }}
                  slotProps={{ legend: { hidden: true } }}
                  sx={{
                    '& .MuiChartsAxis-root': {
                      display: 'none',
                    },
                    '& .MuiBarElement-root': {
                      opacity: 0.92,
                      filter: isDark ? `drop-shadow(0 4px 8px ${alpha(color, 0.18)})` : `drop-shadow(0 3px 6px ${alpha(color, 0.16)})`,
                      transition: 'opacity .2s ease, filter .2s ease',
                    },
                    '& .MuiBarElement-root:hover': { opacity: 1 },
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: '100%',
        backgroundColor: themeColors.paper,
        border: `1px solid ${themeColors.border}`,
        borderRadius: 2,
        transition: 'background-color 0.3s ease, border-color 0.3s ease',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: themeColors.text,
          mb: 2,
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          transition: 'color 0.3s ease',
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        {icon && (
          <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
            {icon}
          </Box>
        )}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            color: color,
            fontSize: 'clamp(1.05rem, 1.6vw + 0.65rem, 1.75rem)',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
        >
          {loading ? <Skeleton variant="text" width={96} height={40} /> : (typeof value === 'number' ? formatValue(value) : value)}
        </Typography>
        {unit && (
          <Typography
            variant="body2"
            sx={{
              ml: 1,
              color: themeColors.textSecondary,
              fontSize: '0.875rem',
              transition: 'color 0.3s ease',
            }}
          >
            {unit}
          </Typography>
        )}
      </Box>

      {(loading || (data && data.length > 0)) && (
        <Box sx={{ width: '100%', height: 100, mt: 1 }}>
          {loading ? (
            <Skeleton variant="rounded" height={100} sx={{ borderRadius: 2 }} />
          ) : (
            <BarChart
              dataset={data}
              height={100}
              margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
              xAxis={[{ scaleType: 'band', dataKey: 'name', categoryGapRatio: 0.28 }]}
              yAxis={[{}]}
              series={[{ dataKey: 'value', color, borderRadius: 5 }]}
              grid={{ horizontal: false, vertical: false }}
              slotProps={{ legend: { hidden: true } }}
              sx={{
                '& .MuiChartsAxis-root': { display: 'none' },
                '& .MuiBarElement-root': {
                  opacity: 0.9,
                  filter: isDark ? `drop-shadow(0 4px 8px ${alpha(color, 0.16)})` : `drop-shadow(0 3px 6px ${alpha(color, 0.14)})`,
                },
              }}
            />
          )}
        </Box>
      )}
    </Paper>
  );
};

export default DashboardMetricCard;
